/**
 * Depth Push — View Transition types for Discovery ↔ Event/Venue only.
 * Admin / Vendor / Marketing / other consumer screens stay instant (no VT).
 *
 * Returning `false` skips `document.startViewTransition` entirely when typed VT
 * is supported (TanStack API). For Safari / older Chrome (no type support),
 * {@link attachDepthPushVtBridge} forces `shouldViewTransition = false` for the
 * same cases and sets `data-pulse-vt` so untyped VT still gets directional CSS.
 */

import type { AnyRouter } from "@tanstack/react-router";

const DETAIL_PATH = /^\/(event|venue)\/[^/]+/;
const PULSE_VT_ATTR = "data-pulse-vt";
const SHARED_HERO_VT_NAME = "pulse-detail-hero";

/** Paths that must never get the consumer depth push (shells / ops). */
function isExcludedPath(pathname: string): boolean {
  return (
    pathname.startsWith("/admin") ||
    pathname.startsWith("/vendor") ||
    pathname.startsWith("/user/") ||
    pathname.startsWith("/for-venues") ||
    pathname.startsWith("/legal") ||
    pathname.startsWith("/dev") ||
    pathname === "/about" ||
    pathname === "/welcome"
  );
}

export function isEventOrVenueDetailPath(pathname: string): boolean {
  return DETAIL_PATH.test(pathname);
}

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** True after Discovery (`/`) has mounted at least once this session. */
let discoveryVisitedThisSession = false;

/** Next navigation to `/` is a cold remount (deep-link back fallback) — skip VT. */
let skipNextHomePopVt = false;

let vtAttrClearTimer: ReturnType<typeof setTimeout> | null = null;

export function markDiscoveryVisited(): void {
  discoveryVisitedThisSession = true;
}

export function hasVisitedDiscoveryThisSession(): boolean {
  return discoveryVisitedThisSession;
}

/** Call before `navigate({ to: "/" })` when history cannot go back. */
export function markColdHomeNavigation(): void {
  skipNextHomePopVt = true;
}

function clearColdHomeNavigation(): void {
  skipNextHomePopVt = false;
}

function setPulseVtAttr(type: "depth-push" | "depth-pop"): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute(PULSE_VT_ATTR, type);
  if (vtAttrClearTimer != null) clearTimeout(vtAttrClearTimer);
  vtAttrClearTimer = setTimeout(() => {
    if (document.documentElement.getAttribute(PULSE_VT_ATTR) === type) {
      document.documentElement.removeAttribute(PULSE_VT_ATTR);
    }
    vtAttrClearTimer = null;
  }, 480);
}

function clearPulseVtAttr(): void {
  if (typeof document === "undefined") return;
  document.documentElement.removeAttribute(PULSE_VT_ATTR);
  if (vtAttrClearTimer != null) {
    clearTimeout(vtAttrClearTimer);
    vtAttrClearTimer = null;
  }
}

/**
 * Arm shared-element hero morph on the Discovery card about to open.
 * Detail hero uses the same name via {@link detailHeroViewTransitionStyle}.
 */
export function armCardHeroViewTransition(mediaEl: HTMLElement | null): void {
  if (!mediaEl || prefersReducedMotion()) return;
  mediaEl.style.viewTransitionName = SHARED_HERO_VT_NAME;
  window.setTimeout(() => {
    if (mediaEl.style.viewTransitionName === SHARED_HERO_VT_NAME) {
      mediaEl.style.viewTransitionName = "";
    }
  }, 480);
}

export function detailHeroViewTransitionStyle(): { viewTransitionName: string } | undefined {
  if (prefersReducedMotion()) return undefined;
  return { viewTransitionName: SHARED_HERO_VT_NAME };
}

/**
 * TanStack `defaultViewTransition.types` callback.
 * - depth-push: into event/venue detail
 * - depth-pop: back out of event/venue detail
 * - false: skip VT (instant swap, same as today)
 */
export function depthPushViewTransitionTypes({
  fromLocation,
  toLocation,
}: {
  fromLocation?: { pathname: string };
  toLocation: { pathname: string };
  pathChanged?: boolean;
  hrefChanged?: boolean;
  hashChanged?: boolean;
}): string[] | false {
  if (prefersReducedMotion()) return false;

  const from = fromLocation?.pathname ?? "";
  const to = toLocation.pathname;

  if (!from || isExcludedPath(from) || isExcludedPath(to)) {
    return false;
  }

  const fromDetail = isEventOrVenueDetailPath(from);
  const toDetail = isEventOrVenueDetailPath(to);

  if (!fromDetail && toDetail) return ["depth-push"];

  if (fromDetail && !toDetail) {
    // `/` always remounts via Outlet (no keep-alive). Never VT over that remount.
    if (to === "/" || skipNextHomePopVt || !discoveryVisitedThisSession) {
      return false;
    }
    return ["depth-pop"];
  }

  // event ↔ venue: treat as push (forward stack feel)
  if (fromDetail && toDetail && from !== to) return ["depth-push"];

  return false;
}

type RouterWithVtFlag = AnyRouter & { shouldViewTransition?: boolean | object };

/**
 * Bridge for browsers without `:active-view-transition-type()`:
 * mirrors the types decision onto `data-pulse-vt` and forces VT off when types
 * would return false (TanStack only honors `types: false` when types are supported).
 */
export function attachDepthPushVtBridge(router: AnyRouter): void {
  if (typeof window === "undefined") return;
  const r = router as RouterWithVtFlag;
  r.subscribe("onBeforeNavigate", (event) => {
    const result = depthPushViewTransitionTypes({
      fromLocation: event.fromLocation,
      toLocation: event.toLocation,
    });
    // Cold-home flag is only for this navigation; clear after the decision.
    clearColdHomeNavigation();
    if (result === false) {
      clearPulseVtAttr();
      // Only force-off when navigate() did not already set a boolean.
      if (r.shouldViewTransition === undefined) {
        r.shouldViewTransition = false;
      }
      return;
    }
    setPulseVtAttr(result[0] as "depth-push" | "depth-pop");
  });
}
